import React, { useState, useEffect, useRef, useMemo } from "react";

import { Icon } from "components/Icon";
import { Alert } from "services/Alert";
import styled from "styled-components";

import { MANAGER_ACCOUNT_USER_CREATE, MANAGER_ACCOUNT_USER_MODIFY } from "const/apiMethods";
import { compareTwoArrays } from "utils";
import { useWindowWidth } from "hooks";
import { api } from "api/loginRoutes";
import CustomField from "components/Form/CustomField";
import { Spacer } from "components/base/Spacer";
import { Modal } from "components/Modal";

import { Button, Checkbox, CheckPicker, FlexboxGrid, Schema, Tooltip } from "rsuite";
import Whisper from "components/Whisper";
import Form from "components/Form/CompatForm";

const { StringType, ArrayType } = Schema.Types;
const defaultFormValue = {};

const MAX_EMAIL_LENGTH = 256;


export default ({
    show,
    onClose,
    userId,
    maxWidth = 600,
    onUpdate,
    adminRoleList,
    userFormValue,
    ...props
}) => {

    const formRef = useRef(null);
    const passwordFormRef = useRef(null);

    const resizedWidth = useWindowWidth();
    const [loading, setLoading] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(!!userId);
    const [formValue, setFormValue] = useState(defaultFormValue);
    const [passwordFormValue, setPasswordFormValue] = useState(defaultFormValue);
    const [isActive, setIsActive] = useState(false);
    const [uniqueRoleList, setUniqueRoleList] = useState([]);

    const formPasswordModel = useMemo(() => Schema.Model({
        password: StringType()
            .pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[\d])[A-Za-z"\d#?!_@$%^&*-]{8,}$/, "At least one number and uppercase and lowercase letter")
            .minLength(8, "Password must be at least 8 characters")
            .addRule((value) => !!userId || !!value, "Is required", true),
    }), [userId]);

    const formCreateModifyModel = useMemo(() => Schema.Model({
        name: StringType().isRequired("Is required"),
        telegram_username: StringType()
            .pattern(/.*\B@(?=\w{5,32}\b)[a-zA-Z0-9]+(?:_[a-zA-Z0-9]+)*.*/, "Telegram username must be 5–32 characters long and start with \"@\"."),
        email: StringType()
            .isRequired("Is required")
            .isEmail("Please enter an email address")
            .maxLength(MAX_EMAIL_LENGTH, `The maximum of this field is ${MAX_EMAIL_LENGTH} characters`),
        login: StringType().isRequired("Is required"),
        role_list: ArrayType().isRequired("Is required"),
    }), []);


    useEffect(() => {
        setFormValue(userFormValue ? { ...userFormValue } : defaultFormValue);
        setPasswordFormValue(defaultFormValue);
        setIsActive(userFormValue ? userFormValue.active : false);

        const unitedRoleList = [...(adminRoleList || []), ...(userFormValue?.role_list || [])];
        const uniqueRoleList = [...new Set(unitedRoleList)];
        setUniqueRoleList(uniqueRoleList);
    }, [userFormValue, adminRoleList]);

    useEffect(() => {
        setShowChangePassword(!userId);
    }, [userId]);

    const rolesUpdated = useMemo(() => uniqueRoleList.length
        ? uniqueRoleList.map((x) => ({ value: x, label: x }))
        : [], [uniqueRoleList, adminRoleList]);

    const onChangeRoleList = (roleList) => {
        const roleDifference = roleList.filter(x => !notExistRoleList.includes(x));

        if (compareTwoArrays(roleList, roleDifference)) {
            setUniqueRoleList(adminRoleList);
        }
    };

    const handleSuccess = () => {
        if ([passwordFormRef.current.check(), formRef.current.check()].includes(false)) {
            return;
        }

        if (passwordFormValue.password
            && passwordFormValue.password !== passwordFormValue.confirm_password) {
            return;
        }

        const formData = { ...formValue };

        const method = userId
            ? MANAGER_ACCOUNT_USER_MODIFY
            : MANAGER_ACCOUNT_USER_CREATE;

        const params = {
            ...formData,
            role_list: formData.role_list,
            active: isActive,
            telegram_username: formData?.telegram_username ? formData.telegram_username : null
        };
        if (passwordFormValue.password) {
            params.password = passwordFormValue.password;
        }
        if (userId) {
            params.target = { account_user_id: userId };
        }

        setLoading(true);

        api(method, params)
            .then((r) => {
                if (!r) {
                    return;
                }

                if (r?.error) {
                    setIsActive(false);
                    onClose();
                    return;
                }

                Alert.success(userId
                    ? <>User <b>{params.login}</b> was modified</>
                    : <>New user <b>{params.login}</b> was created</>
                );

                onUpdate && onUpdate();
                setFormValue(defaultFormValue);
                setPasswordFormValue(defaultFormValue);
                setIsActive(false);
                onClose();
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const defaultRoleList = userFormValue?.role_list || [];
    const notExistRoleList = defaultRoleList.filter(x => adminRoleList?.length && !adminRoleList.includes(x));

    return (
        <Modal
            show={show}
            title={`Create new User`}
            width={resizedWidth > maxWidth ? maxWidth : resizedWidth}
            footer={true}
            disabled={loading}
            onSuccessClose={false}
            successText={userId ? "Update" : "Create"}
            focusTarget="close"
            onSuccess={handleSuccess}
            onClose={() => {
                setFormValue(defaultFormValue);
                setPasswordFormValue(defaultFormValue);
                setIsActive(false);
                onClose(false);
            }}
            {...props}
        >

            <StyledForm
                ref={formRef}
                model={formCreateModifyModel}
                formValue={formValue}
                onChange={(data) => setFormValue(data)}
            >
                <CustomField
                    label="Name"
                    name="name"
                    placeholder="Name"
                    inline
                />
                <CustomField
                    label="Surname"
                    name="surname"
                    placeholder="Surname"
                />
                {/* <StyledInputWithIcon> */}
                <CustomField
                    label="Telegram"
                    name="telegram_username"
                    placeholder="telegram_username"
                    icon={
                        formValue?.telegram_id_ready ? <Whisper
                            trigger="hover"
                            speaker={<Tooltip>Subscribed</Tooltip>}
                        >
                            <Icon icon="check-circle-o"></Icon>
                        </Whisper> : null
                    }
                />
                <CustomField
                    label="Email"
                    name="email"
                    placeholder="Email"
                />
                <CustomField
                    label="Login"
                    name="login"
                    placeholder="Login"
                />
                <CustomField
                    accepter={CheckPicker}
                    label="Roles"
                    name="role_list"
                    placeholder="Roles"
                    data={rolesUpdated}
                    searchable={false}
                    cleanable={false}
                    onChange={onChangeRoleList}
                />

                <CustomField
                    label="Active"
                    accepter={Checkbox}
                    checked={isActive}
                    onChange={(data, value) => setIsActive(value)}
                />
            </StyledForm>

            <StyledForm
                ref={passwordFormRef}
                model={formPasswordModel}
                // autocomplete="off"
                onChange={setPasswordFormValue}
                formValue={passwordFormValue}
            >
                {!showChangePassword &&
                    <FlexboxGrid style={{ marginTop: "20px", width: "100%" }}>
                        <FlexboxGrid.Item style={{ width: "100%" }}>
                            <Button
                                style={{ width: "100%" }}
                                onClick={() => {
                                    setShowChangePassword(true);
                                }}
                                appearance="ghost"
                                color="violet"
                                disabled={loading}
                            >
                                <Icon icon="pencil" /> Change password
                            </Button>
                        </FlexboxGrid.Item>
                    </FlexboxGrid>
                }

                {showChangePassword &&
                    <>
                        <Spacer />

                        <CustomField
                            name="password"
                            type="password"
                            label="Password"
                            placeholder="Password"
                        />

                        <CustomField
                            errorMessage={passwordFormValue.password
                                && passwordFormValue.password !== passwordFormValue.confirm_password
                                ? "Password and confirm password should be equal!"
                                : null
                            }
                            name="confirm_password"
                            type="password"
                            label="Confirm password"
                            placeholder="Confirm password"
                        />

                        {userId &&
                            <FlexboxGrid style={{ marginTop: "20px", width: "100%" }}>
                                <FlexboxGrid.Item style={{ width: "100%" }}>
                                    <Button
                                        style={{ width: "100%" }}
                                        onClick={() => {
                                            setShowChangePassword(false);
                                            setPasswordFormValue(defaultFormValue);
                                        }}
                                        appearance="ghost"
                                        color="violet"
                                        disabled={loading}
                                    >
                                        <Icon icon="reply" /> Don't change password
                                    </Button>
                                </FlexboxGrid.Item>
                            </FlexboxGrid>
                        }
                    </>
                }
            </StyledForm>
        </Modal>
    );
};

const StyledForm = React.forwardRef((props, ref) => {
    return (
        <StyledFormWrapper>
            <Form {...props} ref={ref} />
        </StyledFormWrapper>
    );
});

const StyledFormWrapper = styled.div`
    &,
    .rs-form {
        width: 100%;
        padding: 0px 10px;
    }

    .rs-form-group {
        display: flex;
        align-items: center;
        width: 100%;
    }

    .rs-form-control-label,
    .rs-control-label {
        flex: 0 0 128px;
        width: 128px;
        margin-right: 12px !important;
        margin-bottom: 0 !important;
        white-space: nowrap;
    }

    .rs-form-control-wrapper {
        flex: 1 1 0;
        min-width: 0;
        width: 100%;
    }

    .rs-form-control-wrapper > .rs-input,
    .rs-form-control-wrapper > .rs-input-number,
    .rs-form-control-wrapper > .rs-picker,
    .rs-picker-select {
        max-width: 100%;
        width: 100%;
    }
`;


// const StyledInputWithIcon = styled.div`
//     && {
//         position: relative;
//     }

//     && .rs-icon {
//         color: var(--color-success);
//         position: absolute;
//         left: 146px;
//         top: 9px;
//         font-size: 18px;
//     }
// `;
